import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import api from '../services/api';

const RGSReportPage = () => {

  const [loading, setLoading] =
    useState(false);

  const [reportData, setReportData] =
    useState([]);

  const [error, setError] =
    useState(null);

  const [selectedDistricts, setSelectedDistricts] =
    useState([]);

  const [selectedYear, setSelectedYear] =
    useState('all');

  const [selectedQuarter, setSelectedQuarter] =
    useState('all');

  const [selectedScheme, setSelectedScheme] =
    useState('all');

  const [selectedComponent, setSelectedComponent] =
    useState('all');

  const [districtDropdownOpen, setDistrictDropdownOpen] =
    useState(false);


  // =====================================================
  // FETCH REPORT
  // =====================================================

  const fetchReportData = async () => {

    setLoading(true);
    setError(null);

    try {

      const authToken =
        localStorage.getItem('authToken');

      const response = await fetch(
        `${api.BASE_URL}/project/report/generated-codes`,
        {
          method: 'GET',

          headers: {
            'Content-Type':
              'application/json',

            Authorization:
              authToken
          }
        }
      );

      const result = await response.json();

      if (!response.ok || result.error) {
        throw new Error(
          result.message ||
          'Failed to load report'
        );
      }

      setReportData(
        result.data || []
      );

    } catch (err) {
      console.error('Generated code report error:', err );
      setError( err.message ||'Failed to load report data');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchReportData();
  }, []);


  // =====================================================
  // CURRENCY
  // =====================================================

  const formatCurrency = (amount) => {

    const value =
      Number(amount || 0);

    return new Intl.NumberFormat(
      'en-IN',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    ).format(value);
  };


  // =====================================================
  // FIND FINANCIAL-YEAR QUARTER
  //
  // Q1 = Apr, May, Jun
  // Q2 = Jul, Aug, Sep
  // Q3 = Oct, Nov, Dec
  // Q4 = Jan, Feb, Mar
  // =====================================================

  const getQuarter = (row) => {

    const rawMonth =
      String(row?.month || '')
        .trim()
        .toUpperCase();

    // Already stored as quarter
    if (
      rawMonth === 'Q1' ||
      rawMonth.includes('1ST QUARTER')
    ) {
      return 'q1';
    }

    if (
      rawMonth === 'Q2' ||
      rawMonth.includes('2ND QUARTER')
    ) {
      return 'q2';
    }

    if (
      rawMonth === 'Q3' ||
      rawMonth.includes('3RD QUARTER')
    ) {
      return 'q3';
    }

    if (
      rawMonth === 'Q4' ||
      rawMonth.includes('4TH QUARTER')
    ) {
      return 'q4';
    }


    // Numeric month
    const monthNumber =
      Number(rawMonth);

    if (
      !Number.isNaN(monthNumber) &&
      monthNumber >= 1 &&
      monthNumber <= 12
    ) {

      if (
        monthNumber >= 4 &&
        monthNumber <= 6
      ) {
        return 'q1';
      }

      if (
        monthNumber >= 7 &&
        monthNumber <= 9
      ) {
        return 'q2';
      }

      if (
        monthNumber >= 10 &&
        monthNumber <= 12
      ) {
        return 'q3';
      }

      return 'q4';
    }


    // Month name
    const monthCode =
      rawMonth.substring(0, 3);

    if (
      ['APR', 'MAY', 'JUN']
        .includes(monthCode)
    ) {
      return 'q1';
    }

    if (
      ['JUL', 'AUG', 'SEP']
        .includes(monthCode)
    ) {
      return 'q2';
    }

    if (
      ['OCT', 'NOV', 'DEC']
        .includes(monthCode)
    ) {
      return 'q3';
    }

    if (
      ['JAN', 'FEB', 'MAR']
        .includes(monthCode)
    ) {
      return 'q4';
    }


    // Fallback: approved date
    if (row?.approved_date) {

      const date =
        new Date(
          row.approved_date
        );

      const month =
        date.getMonth() + 1;

      if (
        month >= 4 &&
        month <= 6
      ) {
        return 'q1';
      }

      if (
        month >= 7 &&
        month <= 9
      ) {
        return 'q2';
      }

      if (
        month >= 10 &&
        month <= 12
      ) {
        return 'q3';
      }

      return 'q4';
    }

    return '';
  };


  // =====================================================
  // FILTER OPTIONS
  // =====================================================

  const districtOptions =
    useMemo(() => {

      const map =
        new Map();

      reportData.forEach(
        row => {

          const code =
            String(
              row.district_code || ''
            )
              .trim()
              .toUpperCase();

          if (!code) return;

          map.set(
            code,
            {
              code,
              name:
                row.district_name ||
                code
            }
          );
        }
      );

      return Array.from(
        map.values()
      ).sort(
        (a, b) =>
          a.name.localeCompare(
            b.name
          )
      );

    }, [reportData]);


  const yearOptions =
    useMemo(() => {

      return [
        ...new Set(
          reportData
            .map(row => row.year)
            .filter(Boolean)
        )
      ].sort().reverse();

    }, [reportData]);


  const schemeOptions =
    useMemo(() => {

      return [
        ...new Set(
          reportData
            .map(row =>
              String(
                row.scheme || ''
              ).toUpperCase()
            )
            .filter(Boolean)
        )
      ].sort();

    }, [reportData]);


  // =====================================================
  // FILTER DATA
  // =====================================================

  const filteredData =
    useMemo(() => {

      return reportData.filter(
        row => {

          const districtCode =
            String(
              row.district_code ||
              ''
            ).toUpperCase();


          // District
          if (
            selectedDistricts.length >
              0 &&
            !selectedDistricts.includes(
              districtCode
            )
          ) {
            return false;
          }


          // Financial Year
          if (
            selectedYear !== 'all' &&
            String(row.year) !==
              selectedYear
          ) {
            return false;
          }


          // Quarter
          if (
            selectedQuarter !== 'all' &&
            getQuarter(row) !==
              selectedQuarter
          ) {
            return false;
          }


          // Scheme
          if (
            selectedScheme !== 'all' &&
            String(
              row.scheme || ''
            ).toUpperCase() !==
              selectedScheme
          ) {
            return false;
          }


          // Component
          if (
            selectedComponent !== 'all' &&
            String(
              row.component || ''
            ).toLowerCase() !==
              selectedComponent
          ) {
            return false;
          }


          return true;
        }
      );

    }, [
      reportData,
      selectedDistricts,
      selectedYear,
      selectedQuarter,
      selectedScheme,
      selectedComponent
    ]);


  // =====================================================
  // DISTRICT MULTI SELECT
  // =====================================================

  const toggleDistrict = (code) => {

    setSelectedDistricts(
      previous => {

        if (
          previous.includes(code)
        ) {

          return previous.filter(
            item =>
              item !== code
          );
        }

        return [
          ...previous,
          code
        ];
      }
    );
  };


  // =====================================================
  // CLEAR FILTERS
  // =====================================================

  const clearFilters = () => {

    setSelectedDistricts([]);
    setSelectedYear('all');
    setSelectedQuarter('all');
    setSelectedScheme('all');
    setSelectedComponent('all');
  };


  // =====================================================
  // EXCEL / CSV DOWNLOAD
  // =====================================================

  const downloadExcel = () => {

    if (
      filteredData.length === 0
    ) {

      alert(
        'No data available for selected filters'
      );

      return;
    }


    const escapeCsv =
      value => {

        const text =
          String(
            value ?? ''
          );

        return `"${text.replace(
          /"/g,
          '""'
        )}"`;
      };


    const headers = [
      'S.No',
      'Name of Institution',
      'Scheme',
      'Code Generated',
      'Amount'
    ];


    const rows = [
      headers.join(',')
    ];


    filteredData.forEach(
      (row, index) => {

        rows.push(
          [
            index + 1,

            escapeCsv(
              row.institution_name ||
              row.institute ||
              row.district_name ||
              '-'
            ),

            escapeCsv(
              row.scheme || '-'
            ),

            escapeCsv(
              row.code_generated ||
              '-'
            ),

            Number(
              row.amount || 0
            ).toFixed(2)

          ].join(',')
        );
      }
    );


    const csvContent =
      '\uFEFF' +
      rows.join('\n');


    const blob =
      new Blob(
        [csvContent],
        {
          type:
            'text/csv;charset=utf-8;'
        }
      );


    const url =
      URL.createObjectURL(
        blob
      );


    const link =
      document.createElement(
        'a'
      );


    link.href = url;

    link.download =
      `Generated_Code_Report_${new Date()
        .toISOString()
        .split('T')[0]}.csv`;


    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    URL.revokeObjectURL(
      url
    );
  };


  // =====================================================
  // COMPONENT LABEL
  // =====================================================

  const componentLabel = (
    value
  ) => {

    if (value === 'training') {
      return 'Training';
    }

    if (value === 'infra') {
      return 'Infrastructure';
    }

    if (value === 'admin') {
      return 'Admin';
    }

    return value;
  };


  // =====================================================
  // UI
  // =====================================================

  return (

    <div className="p-6 bg-gray-50 min-h-screen">


      {/* Top Buttons */}
      <div className="flex flex-wrap gap-3 mb-5">

        <button
          type="button"
          onClick={fetchReportData}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
        >
          {loading
            ? 'Refreshing...'
            : 'Refresh'}
        </button>


        <button
          type="button"
          onClick={clearFilters}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
        >
          Clear Filters
        </button>


        <button
          type="button"
          onClick={downloadExcel}
          disabled={
            loading ||
            filteredData.length === 0
          }
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300"
        >
          Download Excel
        </button>

      </div>


      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-5">

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">


          {/* District */}
          <div className="relative">

            <label className="block text-xs font-medium text-gray-600 mb-1">
              District
            </label>


            <button
              type="button"
              onClick={() =>
                setDistrictDropdownOpen(
                  previous =>
                    !previous
                )
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-left text-sm"
            >

              {selectedDistricts.length ===
              0
                ? 'All Districts'
                : `${selectedDistricts.length} District(s) Selected`}

            </button>


            {districtDropdownOpen && (

              <div className="absolute z-50 mt-1 w-full min-w-[260px] bg-white border border-gray-300 rounded-md shadow-xl max-h-72 overflow-y-auto">


                {/* All */}
                <label className="flex items-center gap-2 px-3 py-2 bg-blue-50 border-b cursor-pointer">

                  <input
                    type="checkbox"
                    checked={
                      selectedDistricts.length ===
                      0
                    }
                    onChange={() =>
                      setSelectedDistricts(
                        []
                      )
                    }
                  />

                  <span className="text-sm font-medium">
                    All Districts
                  </span>

                </label>


                {districtOptions.map(
                  district => (

                    <label
                      key={
                        district.code
                      }
                      className="flex items-center gap-2 px-3 py-2 border-b hover:bg-gray-50 cursor-pointer"
                    >

                      <input
                        type="checkbox"
                        checked={
                          selectedDistricts.includes(
                            district.code
                          )
                        }
                        onChange={() =>
                          toggleDistrict(
                            district.code
                          )
                        }
                      />

                      <span className="text-sm">
                        <strong>
                          {district.code}
                        </strong>
                        {' - '}
                        {district.name}
                      </span>

                    </label>
                  )
                )}

              </div>
            )}

          </div>


          {/* Financial Year */}
          <div>

            <label className="block text-xs font-medium text-gray-600 mb-1">
              Financial Year
            </label>

            <select
              value={
                selectedYear
              }
              onChange={e =>
                setSelectedYear(
                  e.target.value
                )
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm"
            >

              <option value="all">
                All Years
              </option>

              {yearOptions.map(
                year => (

                  <option
                    key={year}
                    value={year}
                  >
                    {year}
                  </option>

                )
              )}

            </select>

          </div>


          {/* Quarter */}
          <div>

            <label className="block text-xs font-medium text-gray-600 mb-1">
              Quarter
            </label>

            <select
              value={
                selectedQuarter
              }
              onChange={e =>
                setSelectedQuarter(
                  e.target.value
                )
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm"
            >

              <option value="all">
                All Quarters
              </option>

              <option value="q1">
                1st Quarter - Apr to Jun
              </option>

              <option value="q2">
                2nd Quarter - Jul to Sep
              </option>

              <option value="q3">
                3rd Quarter - Oct to Dec
              </option>

              <option value="q4">
                4th Quarter - Jan to Mar
              </option>

            </select>

          </div>


          {/* Scheme */}
          <div>

            <label className="block text-xs font-medium text-gray-600 mb-1">
              Scheme
            </label>

            <select
              value={
                selectedScheme
              }
              onChange={e =>
                setSelectedScheme(
                  e.target.value
                )
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm"
            >

              <option value="all">
                All Schemes
              </option>

              {schemeOptions.map(
                scheme => (

                  <option
                    key={scheme}
                    value={scheme}
                  >
                    {scheme}
                  </option>

                )
              )}

            </select>

          </div>


          {/* Component */}
          <div>

            <label className="block text-xs font-medium text-gray-600 mb-1">
              Component
            </label>

            <select
              value={
                selectedComponent
              }
              onChange={e =>
                setSelectedComponent(
                  e.target.value
                )
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm"
            >

              <option value="all">
                All Components
              </option>

              <option value="training">
                Training
              </option>

              <option value="infra">
                Infrastructure
              </option>

              <option value="admin">
                Admin
              </option>

            </select>

          </div>

        </div>


        <div className="mt-3 text-sm text-gray-500">

          Showing{' '}

          <strong>
            {filteredData.length}
          </strong>

          {' '}of{' '}

          <strong>
            {reportData.length}
          </strong>

          {' '}records

        </div>

      </div>


      {/* Loading */}
      {loading && (

        <div className="text-center mb-5 p-4 bg-blue-50 rounded-lg">

          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600 mx-auto"></div>

          <p className="mt-3 text-blue-700">
            Loading report data...
          </p>

        </div>

      )}


      {/* Error */}
      {error && (

        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>

      )}


      {/* Report Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">

        <div className="bg-green-200 px-4 py-3">

          <h2 className="text-lg font-semibold text-center text-gray-800">

            Generated Code Report

          </h2>

        </div>


        <div className="overflow-x-auto">

          <table className="w-full border-collapse">

            <thead className="bg-gray-100">

              <tr>

                <th className="border border-gray-300 px-3 py-3 text-sm">
                  S.No
                </th>

                <th className="border border-gray-300 px-3 py-3 text-sm">
                  Name of Institution
                </th>

                <th className="border border-gray-300 px-3 py-3 text-sm">
                  Scheme
                </th>

                <th className="border border-gray-300 px-3 py-3 text-sm">
                  Code Generated
                </th>

                <th className="border border-gray-300 px-3 py-3 text-sm">
                  Amount
                </th>

              </tr>

            </thead>


            <tbody>

              {filteredData.length >
              0 ? (

                filteredData.map(
                  (row, index) => (

                    <tr
                      key={
                        row.id ||
                        index
                      }
                      className="hover:bg-gray-50"
                    >

                      <td className="border border-gray-300 px-3 py-2 text-sm text-center">
                        {index + 1}
                      </td>


                      <td className="border border-gray-300 px-3 py-2 text-sm">

                        {row.institution_name ||
                          row.institute ||
                          row.district_name ||
                          '-'}

                      </td>


                      <td className="border border-gray-300 px-3 py-2 text-sm text-center">

                        {row.scheme ||
                          '-'}

                      </td>


                      <td className="border border-gray-300 px-3 py-2 text-sm">

                        {row.code_generated ||
                          '-'}

                        <div className="text-xs text-gray-400 mt-1">

                          {componentLabel(
                            row.component
                          )}

                        </div>

                      </td>


                      <td className="border border-gray-300 px-3 py-2 text-sm text-right font-semibold">

                        {formatCurrency(
                          row.amount
                        )}

                      </td>

                    </tr>

                  )
                )

              ) : (

                <tr>

                  <td
                    colSpan="5"
                    className="border border-gray-300 px-3 py-8 text-center text-gray-500"
                  >

                    {loading
                      ? 'Loading...'
                      : 'No data available for selected filters'}

                  </td>

                </tr>

              )}

            </tbody>

          </table>

        </div>

      </div>

    </div>
  );
};


export default RGSReportPage;